import { useEffect, useRef, useState } from "react";
import { AvatarMotion } from "@/lib/avatar/motion";
import { AvatarRenderer } from "@/lib/avatar/renderer";
import { PORTRAIT } from "@/lib/avatar/geometry";
import { cn } from "@/lib/utils";

const reducedMotion = () =>
  document.documentElement.dataset.reducedMotion === "true" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// The approved portrait, animated in the browser by warping the original photo (no generated frames, no avatar service).
// Falls back to the still portrait if WebGL isn't available.
export function JarvisAvatar({ status, levelRef }) {
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const statusRef = useRef(status);
  const [mode, setMode] = useState("loading");
  statusRef.current = status;

  useEffect(() => {
    let raf;
    let alive = true;
    const canvas = canvasRef.current;
    const big = (canvas?.clientWidth || 360) * Math.min(window.devicePixelRatio || 1, 2) > 600;
    loadImage(big ? PORTRAIT.src.large : PORTRAIT.src.small)
      .catch(() => loadImage(PORTRAIT.src.fallback))
      .then((img) => {
        if (!alive) return;
        let renderer;
        try {
          renderer = new AvatarRenderer(canvas, img);
        } catch {
          setMode("static");
          return;
        }
        const motion = new AvatarMotion();
        let lastAttr = 0;
        const frame = (now) => {
          const level = levelRef.current;
          const speech = level?.kind === "speech" && level.playing ? level.value : 0;
          const params = window.__jarvisAvatarOverride || motion.frame(now, statusRef.current, speech, reducedMotion());
          renderer.resize();
          renderer.draw(params);
          if (now - lastAttr > 100 && rootRef.current) {
            lastAttr = now;
            rootRef.current.dataset.mouth = params.open.toFixed(2);
            rootRef.current.dataset.audioPlaying = level?.playing ? "true" : "false";
          }
          raf = requestAnimationFrame(frame);
        };
        setMode("animated");
        raf = requestAnimationFrame(frame);
      })
      .catch(() => alive && setMode("error"));
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, [levelRef]);

  return (
    <div ref={rootRef} className="jarvis-avatar absolute inset-0" data-animation={mode} data-testid="jarvis-avatar">
      <img src={PORTRAIT.placeholder} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl" />
      {mode === "static" && (
        <picture>
          <source srcSet={`${PORTRAIT.src.small} 560w, ${PORTRAIT.src.large} 832w`} type="image/webp" />
          <img src={PORTRAIT.src.fallback} alt="JARVIS" className="jarvis-still absolute inset-0 h-full w-full object-cover" data-testid="jarvis-avatar-image" />
        </picture>
      )}
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="JARVIS"
        className={cn("absolute inset-0 h-full w-full transition-opacity duration-500", mode === "animated" ? "opacity-100" : "opacity-0")}
        data-testid="jarvis-avatar-canvas"
      />
      <div aria-hidden className="jarvis-fade pointer-events-none absolute inset-0" />
    </div>
  );
}
